// retryAxios.js
import axios from 'axios'
// import axios from '../../utils/mockAxios';
import EventBus from '../../utils/EventBus';
import EventNames from '../../utils/EventNames';

/* ─── helpers ──────────────────────────────────────────────────────────── */
const wait = (ms) => {
  return new Promise(f => setTimeout(f, ms))
};

const onFailure = (status, retryNumber, backoff) => {
  console.log(`Aborting Sharepoint request after ${retryNumber + 1} attempts.`)
  let text
  if (status === 400) text = `Application error. Please contact support...`
  if (status === 401) text = `You aren't logged in. Please try logging in again...`
  if (status === 503) text = `We're having issues loading your content. Please refresh the page or try again later.`
  if (status === 409) text = `The item you are trying to udpate or create already exists...`

  EventBus.emit(EventNames.ALERT, {
    text,
    "style": "error",
    "duration": 5000,
    showClose: true
  })
}
/**
 * Wrap a single axios method (e.g. "get", "post") with retry logic.
 *
 * @param {string}          method      axios method to wrap
 * @param {number} [maxRetries=3]       how many retries after the first call
 * @param {number} [delayMs=250]        delay between attempts in ms
 * @returns {Function}                  a drop-in replacement for axios[method]
 */
function wrapMethod(method, maxRetries = 3, delayMs = 250, backoff = 2, shouldThrow = false) {
  return async function (...args) {
    let attempts = 0;

    let retrying = true
    while (retrying) {
      if(attempts > 5) throw new Error('Exceeded retry attempts')
      try {
        // return await mockAxios[method](...args);   // original call
        const res = await axios[method](...args);   // original call
        return res
      } catch (err) {
        const status = err?.response?.status;
        console.log('Sharepoint request failed with:', status)
        if(shouldThrow) throw err

        // Retry only on 503, stop otherwise (or when out of retries)

        const dontRetryOn = [400, 401]
        if(dontRetryOn.includes(status)) retrying = false
        
        if (!retrying || attempts >= maxRetries) {
          onFailure(status, attempts, delayMs)
          retrying = false
        }

        if(retrying && delayMs > 0) await wait(delayMs);
      }

      attempts += 1;
      delayMs = delayMs * backoff

      if(retrying && attempts > 0) {
        console.log(`Retrying Sharepoint request: attempt ${attempts + 1} and backoff ${delayMs}ms`)
      }
    }
  };
}

/* ─── Public helpers ───────────────────────────────────────────────────── */
function withRetryGet(maxRetries, delayMs, backoff, shouldThrow) {
  return wrapMethod('get', maxRetries, delayMs, backoff, shouldThrow);
}

function withRetryPost(maxRetries, delayMs, backoff, shouldThrow) {
  return wrapMethod('post', maxRetries, delayMs, backoff, shouldThrow);
}

function withRetryPatch(maxRetries, delayMs, backoff, shouldThrow) {
  return wrapMethod('patch', maxRetries, delayMs, backoff, shouldThrow);
}

function withRetryPut(maxRetries, delayMs, backoff, shouldThrow) {
  return wrapMethod('put', maxRetries, delayMs, backoff, shouldThrow);
}

function withRetryDelete(maxRetries, delayMs, backoff, shouldThrow) {
  return wrapMethod('delete', maxRetries, delayMs, backoff, shouldThrow);
}

function createRetryAxios(shouldThrow = false, maxRetries = 3, delayMs = 250, backoff = 2) {
  return {
    get: withRetryGet(maxRetries, delayMs, backoff, shouldThrow),
    post: withRetryPost(maxRetries, delayMs, backoff, shouldThrow),
    patch: withRetryPatch(maxRetries, delayMs, backoff, shouldThrow),
    put: withRetryPut(maxRetries, delayMs, backoff, shouldThrow),
    delete: withRetryDelete(maxRetries, delayMs, backoff, shouldThrow),
  };
}

/* ─── Exports ──────────────────────────────────────────────────────────── */
export default createRetryAxios
